import React, { useState, useEffect } from "react";
import api from "../../lib/axios";
import { toast } from "sonner";
import {
  FileText,
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Hash
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewQuotationModal = ({ isOpen, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    client_company: "",
    client_contact: "",
    client_address: "",
    client_phone: "",
    client_email: "",
    quote_date: new Date().toISOString().split('T')[0]
  });

  // Fetch next code when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNextCode();
    }
  }, [isOpen]);

  const fetchNextCode = async () => {
    try {
      const response = await api.get(`${API}/quotations/next-code`);
      setFormData(prev => ({ ...prev, code: response.data.next_code }));
    } catch (error) {
      console.error("Error fetching next code:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name) {
      toast.error("El código y nombre son requeridos");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`${API}/quotations`, formData);
      toast.success("Cotización creada exitosamente");
      onCreated(response.data.quotation);
      // Reset form
      setFormData({
        code: "",
        name: "",
        client_company: "",
        client_contact: "",
        client_address: "",
        client_phone: "",
        client_email: "",
        quote_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      // console.error("Error creating quotation:", error);
      const message = error.response?.data?.detail || "Error al crear cotización";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      code: "",
      name: "",
      client_company: "",
      client_contact: "",
      client_address: "",
      client_phone: "",
      client_email: "",
      quote_date: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Nuevo Proyecto de Cotización
          </DialogTitle>
          <DialogDescription>
            Ingresa los datos del proyecto y cliente para crear una nueva cotización
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Project Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">
              Información del Proyecto
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  Código de Cotización *
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  placeholder="COT-001"
                  data-testid="quotation-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quote_date" className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Fecha
                </Label>
                <Input
                  id="quote_date"
                  type="date"
                  value={formData.quote_date}
                  onChange={(e) => handleChange('quote_date', e.target.value)}
                  data-testid="quotation-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Nombre del Proyecto *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ej: Edificio Skyline - Ventanas"
                data-testid="quotation-name-input"
              />
            </div>
          </div>

          {/* Client Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">
              Información del Cliente
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_company" className="flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Empresa
                </Label>
                <Input
                  id="client_company"
                  value={formData.client_company}
                  onChange={(e) => handleChange('client_company', e.target.value)}
                  placeholder="Nombre de la empresa"
                  data-testid="quotation-company-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_contact" className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Persona de Contacto
                </Label>
                <Input
                  id="client_contact"
                  value={formData.client_contact}
                  onChange={(e) => handleChange('client_contact', e.target.value)}
                  placeholder="Nombre del contacto"
                  data-testid="quotation-contact-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_address" className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Dirección
              </Label>
              <Input
                id="client_address"
                value={formData.client_address}
                onChange={(e) => handleChange('client_address', e.target.value)}
                placeholder="Dirección completa"
                data-testid="quotation-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_phone" className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  Teléfono
                </Label>
                <Input
                  id="client_phone"
                  value={formData.client_phone}
                  onChange={(e) => handleChange('client_phone', e.target.value)}
                  placeholder="+51 999 999 999"
                  data-testid="quotation-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_email" className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Email
                </Label>
                <Input
                  id="client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => handleChange('client_email', e.target.value)}
                  placeholder="cliente@empresa.com"
                  data-testid="quotation-email-input"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-quotation-btn"
          >
            {loading ? "Creando..." : "Crear Cotización"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewQuotationModal;
